<template>
  <div class="wf-flow-viewer">
    <div class="wf-vnode wf-start">
      <div class="wf-dot done"><i class="el-icon-user-solid"></i></div>
      <div class="wf-card done">
        <div class="wf-card-title">发起申请</div>
        <div class="wf-card-sub">
          {{ instance.initiatorName || '—' }}
          <span v-if="instance.createTime" class="wf-time">{{ instance.createTime }}</span>
        </div>
      </div>
    </div>

    <div v-for="(n, idx) in renderNodes" :key="n.nodeKey || idx" class="wf-vnode">
      <div class="wf-line" :class="stateOf(idx)"></div>
      <div class="wf-dot" :class="stateOf(idx)">
        <i :class="iconOf(n)"></i>
      </div>
      <div class="wf-card" :class="[stateOf(idx), n.type]">
        <div class="wf-card-title">
          {{ titleOf(n) }}
          <el-tag v-if="n.type === 'approval' && modeText(n)" size="mini" type="info" effect="plain">{{ modeText(n) }}</el-tag>
          <el-tag v-if="n.type === 'cc'" size="mini" type="warning" effect="plain">抄送</el-tag>
        </div>
        <div class="wf-card-sub">
          <template v-if="approversOf(n).length">
            <span v-for="(t, i) in approversOf(n)" :key="i" class="wf-approver">
              {{ t.approverName || ('用户' + t.approverId) }}
              <i :class="taskIcon(t)" :style="{ color: taskColor(t) }"></i>
            </span>
          </template>
          <span v-else class="wf-muted">{{ assigneeSummary(n) }}</span>
        </div>
        <div v-if="endStatusText(n)" class="wf-card-sub wf-end-status">{{ endStatusText(n) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseFlow, parseData, resolvePath, nodeTitle } from './flowUtil'

export default {
  name: 'FlowViewer',
  props: {
    flowJson: { type: [String, Object], default: '' },
    instance: { type: Object, default: () => ({}) },
    tasks: { type: Array, default: () => [] }
  },
  computed: {
    path() {
      const root = parseFlow(this.flowJson)
      if (!root) return []
      const fd = parseData(this.instance.formData)
      return resolvePath(root.child, fd)
    },
    renderNodes() {
      // 去掉末尾 end 节点单独处理为“结束”展示
      return this.path
    },
    curIdx() {
      const key = this.instance.currentNodeKey
      if (!key) return -1
      return this.path.findIndex(n => n.nodeKey === key)
    }
  },
  methods: {
    titleOf(n) {
      if (n.type === 'end') return '流程结束'
      return nodeTitle(n)
    },
    iconOf(n) {
      switch (n.type) {
        case 'approval': return 'el-icon-s-check'
        case 'cc': return 'el-icon-message-solid'
        case 'end': return 'el-icon-flag'
        default: return 'el-icon-more'
      }
    },
    stateOf(idx) {
      const status = this.instance.status
      const ci = this.curIdx
      if (status === 1) return 'done'                       // 全部通过
      if (status === 2) {                                    // 拒绝
        if (ci < 0) return 'pending'
        return idx < ci ? 'done' : (idx === ci ? 'reject' : 'pending')
      }
      if (status === 3) {                                    // 撤销
        if (ci < 0) return 'revoke'
        return idx < ci ? 'done' : 'revoke'
      }
      // 审批中
      if (ci < 0) return 'pending'
      return idx < ci ? 'done' : (idx === ci ? 'current' : 'pending')
    },
    modeText(n) {
      const m = n.props && n.props.approveMode
      if (m === 'and') return '会签'
      if (m === 'or') return '或签'
      return ''
    },
    approversOf(n) {
      return (this.tasks || []).filter(t => t.nodeKey === n.nodeKey)
    },
    assigneeSummary(n) {
      const p = n.props || {}
      const map = {
        user: '指定人员', role: '指定角色', deptLeader: '部门负责人',
        initiatorLeader: '直属上级', form: '表单指定', formDeptLeader: '表单人员部门负责人', self: '发起人自选'
      }
      return map[p.assigneeType] || '—'
    },
    taskIcon(t) {
      switch (t.status) {
        case 1: return 'el-icon-circle-check'
        case 2: return 'el-icon-circle-close'
        case 3: return 'el-icon-view'
        case 4: return 'el-icon-remove-outline'
        default: return 'el-icon-time'
      }
    },
    taskColor(t) {
      switch (t.status) {
        case 1: return '#67C23A'
        case 2: return '#F56C6C'
        case 3: return '#E6A23C'
        case 4: return '#C0C4CC'
        default: return '#409EFF'
      }
    },
    endStatusText(n) {
      if (n.type !== 'end') return ''
      const s = this.instance.status
      if (s === 1) return '✅ 审批通过'
      if (s === 2) return '❌ 已拒绝'
      if (s === 3) return '↩️ 已撤销'
      return '进行中'
    }
  }
}
</script>

<style scoped>
.wf-flow-viewer {
  padding: 8px 0;
}
.wf-vnode {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding-left: 34px;
  min-height: 56px;
}
.wf-line {
  position: absolute;
  left: 11px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #DCDFE6;
}
.wf-line.done { background: #67C23A; }
.wf-line.current { background: #409EFF; }
.wf-vnode:first-child .wf-line { display: none; }
.wf-dot {
  position: absolute;
  left: 0;
  top: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #C0C4CC;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  z-index: 1;
}
.wf-dot.done { background: #67C23A; }
.wf-dot.current { background: #409EFF; animation: wf-pulse 1.4s infinite; }
.wf-dot.reject { background: #F56C6C; }
.wf-dot.revoke { background: #909399; }
.wf-dot.pending { background: #C0C4CC; }
@keyframes wf-pulse {
  0% { box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(64, 158, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(64, 158, 255, 0); }
}
.wf-card {
  background: #fff;
  border: 1px solid #EBEEF5;
  border-left: 3px solid #C0C4CC;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 4px 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  flex: 1;
}
.wf-card.done { border-left-color: #67C23A; }
.wf-card.current { border-left-color: #409EFF; background: #ECF5FF; }
.wf-card.reject { border-left-color: #F56C6C; background: #FEF0F0; }
.wf-card.revoke { border-left-color: #909399; }
.wf-card.cc { border-left-color: #E6A23C; }
.wf-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 6px;
}
.wf-card-sub {
  font-size: 12px;
  color: #606266;
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.wf-approver i { margin-left: 3px; }
.wf-time { color: #909399; margin-left: 8px; }
.wf-muted { color: #909399; }
.wf-end-status { font-weight: 600; }
</style>
